/**
 * @fileoverview Toast notification system context
 */
import React, { createContext, useContext, useState, useCallback } from 'react';
import type { ToastContextValue, ProviderProps, Toast, ToastType } from 'src/types';
import './Toast.css';

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

export const ToastProvider: React.FC<ProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const addToast = useCallback(
    (
      message: string,
      type: ToastType = 'info',
      duration: number = 5000,
      action?: { label: string; onClick: () => void }
    ) => {
      const id = Date.now() + Math.random().toString();
      const toast: Toast = { id, message, type, duration, action };

      setToasts((prev) => [...prev, toast]);

      if (duration > 0) {
        setTimeout(() => {
          removeToast(id);
        }, duration);
      }

      return id;
    },
    [removeToast]
  );

  const success = useCallback(
    (message: string, duration?: number, action?: { label: string; onClick: () => void }) => {
      return addToast(message, 'success', duration, action);
    },
    [addToast]
  );

  const error = useCallback(
    (message: string, duration?: number, action?: { label: string; onClick: () => void }) => {
      return addToast(message, 'error', duration, action);
    },
    [addToast]
  );

  const warning = useCallback(
    (message: string, duration?: number, action?: { label: string; onClick: () => void }) => {
      return addToast(message, 'warning', duration, action);
    },
    [addToast]
  );

  const info = useCallback(
    (message: string, duration?: number, action?: { label: string; onClick: () => void }) => {
      return addToast(message, 'info', duration, action);
    },
    [addToast]
  );

  const value: ToastContextValue = {
    toasts,
    addToast,
    removeToast,
    success,
    error,
    warning,
    info,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
};

interface ToastContainerProps {
  toasts: Toast[];
  removeToast: (id: string) => void;
}

const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, removeToast }) => {
  if (toasts.length === 0) return null;

  return (
    <div className="toast-container">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
      ))}
    </div>
  );
};

interface ToastItemProps {
  toast: Toast;
  onClose: () => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onClose }) => {
  const { type, message, action } = toast;

  const handleActionClick = () => {
    if (action) {
      action.onClick();
      onClose(); // Close toast after action
    }
  };

  const toastClass = `toast-item toast-item--${type}`;

  return (
    <div className={toastClass}>
      <div className="toast-message">{message}</div>

      {action && (
        <button
          onClick={handleActionClick}
          className="toast-action-button"
          aria-label={action.label}
        >
          {action.label}
        </button>
      )}

      <button onClick={onClose} className="toast-close-button" aria-label="Close notification">
        ×
      </button>
    </div>
  );
};
